import { api } from "../app/api";
import { useAsync } from "../app/useAsync";
import { StatusChip } from "../components/StatusChip";

function toneFor(v: string | undefined): "ok" | "warn" | "no" {
  if (v === "PASS") return "ok";
  if (v === "IGNORED") return "warn";
  return "no";
}

export function ShopifySchemaPage() {
  const { data, error, loading } = useAsync(api.shopifySchema, []);
  const val = useAsync(api.schemaValidate, []);

  return (
    <>
      <h1>Shopify Schema</h1>
      <p className="page-sub">
        Read-only verification of the existing <code>sandlot</code> schema.
        Nothing is created or modified here.
      </p>

      <div className="card" style={{ marginBottom: "1.2rem" }}>
        <h3>Migration compatibility</h3>
        {val.loading && <p className="muted">Checking…</p>}
        {val.data?.error && <p className="muted">{val.data.error}</p>}
        {val.data?.summary && (
          <>
            <p style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <StatusChip tone={toneFor(val.data.summary.uuidFields)} label={`UUID fields: ${val.data.summary.uuidFields}`} />
              <StatusChip tone="warn" label="Image fields: IGNORED" />
              <StatusChip tone={toneFor(val.data.summary.jsonFields)} label={`JSON fields: ${val.data.summary.jsonFields}`} />
              <StatusChip tone={toneFor(val.data.summary.metaobjectReferences)} label={`References: ${val.data.summary.metaobjectReferences}`} />
              <StatusChip tone={toneFor(val.data.summary.overall)} label={`Overall: ${val.data.summary.overall}`} />
            </p>
            {val.data.failures && val.data.failures.length > 0 && (
              <table>
                <thead>
                  <tr><th>Field</th><th>Expected</th><th>Live</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {val.data.failures.map((f) => (
                    <tr key={`${f.metaobjectType}.${f.key}`}>
                      <td><code>{f.metaobjectType}.{f.key}</code></td>
                      <td className="muted">{f.expected ?? "—"}</td>
                      <td className="muted">{f.live ?? "—"}</td>
                      <td><StatusChip tone="no" label={f.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {val.data.imageFieldsIgnored && val.data.imageFieldsIgnored.length > 0 && (
              <p className="muted" style={{ fontSize: "0.82rem", marginTop: 8 }}>
                Ignored image fields: {val.data.imageFieldsIgnored.join(", ")}
              </p>
            )}
          </>
        )}
      </div>

      {loading && <p className="muted">Loading…</p>}
      {error && <p className="muted">Unavailable: {error}</p>}
      {data && !data.configured && (
        <div className="banner">Shopify is not configured — cannot read schema.</div>
      )}
      {data?.error && <p className="muted">Error: {data.error}</p>}

      {data?.customerMetafields && (
        <div className="card" style={{ marginBottom: "1.2rem" }}>
          <h3>Customer metafield definitions (sandlot.*)</h3>
          <p style={{ margin: "0 0 10px" }}>
            {data.customerMetafields.missing.length === 0 ? (
              <StatusChip tone="ok" label="All expected present" />
            ) : (
              <StatusChip
                tone="warn"
                label={`${data.customerMetafields.missing.length} missing`}
              />
            )}
          </p>
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Type</th>
                <th>Present</th>
              </tr>
            </thead>
            <tbody>
              {data.customerMetafields.expected.map((key) => {
                const found = data.customerMetafields!.found.find(
                  (f) => f.key === key,
                );
                return (
                  <tr key={key}>
                    <td>
                      <code>sandlot.{key}</code>
                    </td>
                    <td className="muted">{found?.type ?? "—"}</td>
                    <td>
                      {found ? (
                        <StatusChip tone="ok" label="Yes" />
                      ) : (
                        <StatusChip tone="warn" label="Missing" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data?.metaobjects && (
        <div className="card">
          <h3>Metaobject definitions</h3>
          <p style={{ margin: "0 0 10px" }}>
            {data.metaobjects.missing.length === 0 ? (
              <StatusChip tone="ok" label="All expected present" />
            ) : (
              <StatusChip
                tone="warn"
                label={`${data.metaobjects.missing.length} missing`}
              />
            )}
          </p>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Fields</th>
                <th>Present</th>
              </tr>
            </thead>
            <tbody>
              {data.metaobjects.expected.map((type) => {
                const found = data.metaobjects!.found.find(
                  (f) => f.type === type,
                );
                return (
                  <tr key={type}>
                    <td>
                      <code>{type}</code>
                    </td>
                    <td className="muted">
                      {found ? found.fieldKeys.length : "—"}
                    </td>
                    <td>
                      {found ? (
                        <StatusChip tone="ok" label="Yes" />
                      ) : (
                        <StatusChip tone="warn" label="Missing" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
